import { useEffect, useRef } from "react";

/**
 * useAnalyzingSound  —  character: "Soothing"
 *
 * A warm, settled pad while the tone analysis is in flight. Web Audio API
 * only — no audio assets, zero network weight, no licensing.
 *
 * Voicing  : major-chord triad (root, major third, octave) at 262 / 330 / 524 Hz
 *            — the major third reads as gentle and consoling rather than urgent.
 * Detune   : −7 cents on the third for a natural chorus shimmer.
 * Filter   : low-pass at 1500 Hz, Q 0.8 — warm, no edge.
 * Breath   : slow LFO at 0.18 Hz modulating master gain (depth ≈ ⅓ of master).
 * Envelope : 600 ms fade-in, 400 ms fade-out — the sound *settles* in.
 * Loudness : master gain 0.18 — clearly audible at typical laptop volume but
 *            well below notification levels.
 *
 * - Lazily creates an AudioContext on first start (browser autoplay rules)
 * - No-op when `enabled` is false
 */
export function useAnalyzingSound(active, enabled) {
    const ctxRef = useRef(null);
    const nodesRef = useRef(null);

    useEffect(() => {
        if (!active || !enabled) {
            stopSound(ctxRef, nodesRef);
            return;
        }
        startSound(ctxRef, nodesRef);
        return () => stopSound(ctxRef, nodesRef);
    }, [active, enabled]);

    // Tear down on unmount
    useEffect(() => {
        return () => stopSound(ctxRef, nodesRef, /*disposeCtx*/ true);
    }, []);
}

function getCtx(ctxRef) {
    if (ctxRef.current && ctxRef.current.state !== "closed") {
        return ctxRef.current;
    }
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) return null;
    ctxRef.current = new Ctor();
    return ctxRef.current;
}

function startSound(ctxRef, nodesRef) {
    if (nodesRef.current) return; // guard against overlap

    const ctx = getCtx(ctxRef);
    if (!ctx) return;
    if (ctx.state === "suspended") ctx.resume().catch(() => {});

    const now = ctx.currentTime;

    // Master output gain — overall pad volume + slow envelope.
    const master = ctx.createGain();
    master.gain.setValueAtTime(0, now);
    master.gain.linearRampToValueAtTime(0.18, now + 0.6);
    master.connect(ctx.destination);

    // Warm low-pass filter — rolls off any edge without dulling presence.
    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.setValueAtTime(1500, now);
    filter.Q.setValueAtTime(0.8, now);
    filter.connect(master);

    // Voicing: root + major third + octave (consonant major chord).
    const osc1 = ctx.createOscillator();
    osc1.type = "sine";
    osc1.frequency.setValueAtTime(262, now); // root  (C4)

    const osc2 = ctx.createOscillator();
    osc2.type = "sine";
    osc2.frequency.setValueAtTime(330, now); // major third (E4)
    osc2.detune.setValueAtTime(-7, now);     // tiny detune → gentle chorus shimmer

    const osc3 = ctx.createOscillator();
    osc3.type = "sine";
    osc3.frequency.setValueAtTime(524, now); // octave (C5)

    // Per-voice gains: root anchors, third and octave float gently above.
    const g1 = ctx.createGain();
    g1.gain.setValueAtTime(0.7, now);
    const g2 = ctx.createGain();
    g2.gain.setValueAtTime(0.4, now);
    const g3 = ctx.createGain();
    g3.gain.setValueAtTime(0.22, now);

    osc1.connect(g1).connect(filter);
    osc2.connect(g2).connect(filter);
    osc3.connect(g3).connect(filter);

    // Slow breath — 0.18 Hz feels like a relaxed exhale.
    const lfo = ctx.createOscillator();
    lfo.type = "sine";
    lfo.frequency.setValueAtTime(0.18, now);
    const lfoGain = ctx.createGain();
    lfoGain.gain.setValueAtTime(0.06, now); // depth ≈ ⅓ of master
    lfo.connect(lfoGain).connect(master.gain);

    osc1.start(now);
    osc2.start(now);
    osc3.start(now);
    lfo.start(now);

    nodesRef.current = { master, filter, osc1, osc2, osc3, g1, g2, g3, lfo, lfoGain };
}

function stopSound(ctxRef, nodesRef, disposeCtx = false) {
    const nodes = nodesRef.current;
    const ctx = ctxRef.current;
    nodesRef.current = null;
    if (!nodes || !ctx) {
        if (disposeCtx && ctx && ctx.state !== "closed") {
            ctx.close().catch(() => {});
            ctxRef.current = null;
        }
        return;
    }

    const now = ctx.currentTime;
    try {
        // Cancel any LFO modulation still scheduling onto master.gain
        nodes.master.gain.cancelScheduledValues(now);
        nodes.master.gain.setValueAtTime(nodes.master.gain.value, now);
        nodes.master.gain.linearRampToValueAtTime(0, now + 0.4);
        nodes.osc1.stop(now + 0.45);
        nodes.osc2.stop(now + 0.45);
        if (nodes.osc3) nodes.osc3.stop(now + 0.45);
        nodes.lfo.stop(now + 0.45);
    } catch {
        /* nodes may already be stopped */
    }
    if (disposeCtx && ctx.state !== "closed") {
        window.setTimeout(() => {
            ctx.close().catch(() => {});
            ctxRef.current = null;
        }, 500);
    }
}
